import {
  MODULE_DIRECTION_STRAIGHT,
  MODULE_FAMILY_CORRIDOR,
  MODULE_FAMILY_ROOM,
  getPickableElements,
  type MapEditorCatalogElement,
} from './mapEditorCatalog';

// The picker asks three questions — what kind of module, which way it turns, how big — and
// then adds the module that answers all three. UE deliberately ships no ready-made selection
// tree: the catalog carries the facets, and how they are asked about is the web's to decide,
// because the popup flow starts here.
//
// Everything below groups on the values it actually finds rather than on a fixed list, so a
// build that ships a family or size this hub has never heard of stays pickable.
export type ModuleFacetKey = 'family' | 'direction' | 'size';

// The order the questions are asked in, and the order answers are shown back in. Exported so the
// popup's breadcrumb reads the same list the flow walks, rather than repeating it — a second copy
// would put the answers in a different order than they were given the moment this one changed.
export const FACET_ORDER: ModuleFacetKey[] = ['family', 'direction', 'size'];

// The order options are offered in when the catalog carries them. Anything not named here
// keeps its catalog order and follows, so an unknown value is listed rather than dropped.
const PREFERRED_VALUES: Record<ModuleFacetKey, string[]> = {
  family: [MODULE_FAMILY_ROOM, MODULE_FAMILY_CORRIDOR],
  direction: [MODULE_DIRECTION_STRAIGHT, 'left', 'right'],
  size: ['S', 'M', 'L'],
};

export interface ModuleFacetSelection {
  family?: string;
  direction?: string;
  size?: string;
}

export interface ModuleFacetOption {
  // The facet value on the step questions, the element id on the final one.
  value: string;
  // Every module still reachable by choosing this option. Carried so the UI can show what an
  // option leads to — spot counts, geometry warnings — without re-filtering.
  elements: MapEditorCatalogElement[];
}

/**
 * Narrows the catalog to the modules matching every facet chosen so far.
 *
 * @param elements - Pickable modules; caps must already be filtered out.
 * @param selection - Facets chosen so far. An unset facet matches everything.
 * @returns The matching modules, in catalog order.
 */
export const matchModules = (
  elements: MapEditorCatalogElement[],
  selection: ModuleFacetSelection,
): MapEditorCatalogElement[] =>
  elements.filter((element) =>
    FACET_ORDER.every((key) => {
      const wanted = selection[key];
      return wanted === undefined || element[key] === wanted;
    }),
  );

/**
 * The distinct answers to one facet question, given the answers already chosen.
 *
 * @param elements - Pickable modules; caps must already be filtered out.
 * @param key - Facet being asked about.
 * @param selection - Facets chosen so far.
 * @returns One option per distinct value, preferred values first and the rest in catalog order.
 */
export const getFacetOptions = (
  elements: MapEditorCatalogElement[],
  key: ModuleFacetKey,
  selection: ModuleFacetSelection,
): ModuleFacetOption[] => {
  const byValue = new Map<string, MapEditorCatalogElement[]>();

  matchModules(elements, selection).forEach((element) => {
    const bucket = byValue.get(element[key]);
    if (bucket) {
      bucket.push(element);
      return;
    }
    byValue.set(element[key], [element]);
  });

  const preferred = PREFERRED_VALUES[key];

  return Array.from(byValue.entries())
    .map(([value, bucket]) => ({ value, elements: bucket }))
    .sort((a, b) => {
      const rankA = preferred.indexOf(a.value);
      const rankB = preferred.indexOf(b.value);
      if (rankA === rankB) {
        return 0;
      }
      // An unranked value sorts after every ranked one, and keeps its catalog position
      // relative to the other unranked ones because the sort is stable.
      if (rankA === -1) {
        return 1;
      }
      if (rankB === -1) {
        return -1;
      }
      return rankA - rankB;
    });
};

export interface ModuleTreeNode {
  // Stable across renders and unique within the tree, because it is the path taken to reach the
  // node. It is what the tree remembers as expanded.
  key: string;
  // The catalog's own category string at the top level, and the raw facet value below it. Both are
  // labelled by the caller: a category is authored text shown as-is, a facet value has a
  // translation.
  label: string;
  // Which question this level answers. Absent on a category node, which answers none.
  facet?: ModuleFacetKey;
  // Every module reachable through this node.
  elements: MapEditorCatalogElement[];
  // Empty at a leaf. A leaf standing for one module adds it; a leaf standing for several is a set
  // of variants that the facets cannot tell apart — see buildModuleTree.
  children: ModuleTreeNode[];
}

/**
 * Inserts a level for the next question that tells the given modules apart, and recurses.
 *
 * Deliberately keyed off the module subset alone rather than a running selection: a facet already
 * fixed further up has one distinct value left in the subset, so the single-answer rule skips it
 * without anything having to remember that it was asked.
 *
 * @param elements - Modules under the node being built.
 * @param keyPrefix - Path of the node being built, which its children extend.
 * @returns The child levels, or an empty array when no facet distinguishes these modules.
 */
const buildFacetLevels = (
  elements: MapEditorCatalogElement[],
  keyPrefix: string,
): ModuleTreeNode[] => {
  const question = FACET_ORDER.map((key) => ({
    key,
    options: getFacetOptions(elements, key, {}),
  })).find((step) => step.options.length > 1);

  if (!question) {
    return [];
  }

  return question.options.map((option) => {
    const key = `${keyPrefix}/${question.key}:${option.value}`;

    return {
      key,
      label: option.value,
      facet: question.key,
      elements: option.elements,
      children: buildFacetLevels(option.elements, key),
    };
  });
};

/**
 * The pickable modules as a tree: category, then only the questions that tell modules apart.
 *
 * The shape the kit produces rather than a fixed depth — corridors come out as
 * `Corridors → Turns left → Short`, because their direction and size both distinguish, while
 * rooms come out as `Galleries → Small`, because every room in the kit is straight and asking
 * would be a click that decides nothing. A category holding a single module is itself a leaf.
 *
 * A leaf covering several modules is the wall-layout case. Variants of one room — the same
 * family, direction and size, differing only in how many brandable spots the level was authored
 * with — are separate catalog elements by design, so nothing in the facets can separate them and
 * they arrive here together. That is what the caller opens the wall-layout step for.
 *
 * @param elements - The whole catalog; caps are filtered out here.
 * @returns Category nodes sorted by name, each with its own subtree.
 */
export const buildModuleTree = (
  elements: MapEditorCatalogElement[],
): ModuleTreeNode[] => {
  const byCategory = new Map<string, MapEditorCatalogElement[]>();

  getPickableElements(elements).forEach((element) => {
    const bucket = byCategory.get(element.category);
    if (bucket) {
      bucket.push(element);
      return;
    }
    byCategory.set(element.category, [element]);
  });

  // Sorted rather than left in catalog order: the categories are the one level an author reads as
  // a menu, and the kit's authoring order is not a menu order.
  return Array.from(byCategory.keys())
    .sort((a, b) => a.localeCompare(b))
    .map((category) => {
      const bucket = byCategory.get(category) ?? [];

      return {
        key: `category:${category}`,
        label: category,
        elements: bucket,
        children: buildFacetLevels(bucket, `category:${category}`),
      };
    });
};
